import {
  IBooking,
  IBookingListParams,
  IBookingReasonPayload,
  ICancelBookingResult,
  ICompleteBookingPayload,
  ICreateBookingPayload,
  ICreateBookingResult,
  IPaginatedResponse,
  IProposeBookingTimePayload
} from '@pharvio/core/shared';
import { Observable } from 'rxjs';

/**
 * Staff bookings. Deliberately has no `updateBooking` — the service, time,
 * customer and price are snapshotted at creation so the deposit split cannot
 * shift under a paid order. Rescheduling is `proposeTime`; anything else is
 * cancel and rebook.
 *
 * Nothing here takes a payment either: settle `booking.depositOrder` /
 * `booking.balanceOrder` through the ordinary order endpoints.
 */
export abstract class BookingsDataService {
  abstract getBookings(
    params?: IBookingListParams
  ): Observable<IPaginatedResponse<IBooking>>;

  abstract getBooking(id: string): Observable<IBooking>;

  /**
   * Returns `paymentLinkUrl: null` always — staff legs never auto-initialise
   * Paystack. Collect the deposit from `result.booking.depositOrder`.
   */
  abstract createBooking(
    payload: ICreateBookingPayload
  ): Observable<ICreateBookingResult>;

  /** Confirms at `proposedAt` when present, else `requestedAt`. */
  abstract confirm(id: string): Observable<IBooking>;

  abstract proposeTime(
    id: string,
    payload: IProposeBookingTimePayload
  ): Observable<IBooking>;

  abstract start(id: string): Observable<IBooking>;

  /**
   * Marks the service rendered. If a balance is due it raises `balanceOrder`
   * and the booking sits in `pending_balance`; if nothing is due it goes
   * straight to `completed`. Read `result.booking.status` — do not assume.
   */
  abstract complete(
    id: string,
    payload: ICompleteBookingPayload
  ): Observable<ICreateBookingResult>;

  /** The three below return the booking plus `depositInvoiceId`. */
  abstract decline(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult>;

  /** Refused with 409 `BOOKING_NOT_YET_DUE` before the scheduled time. */
  abstract noShow(id: string): Observable<ICancelBookingResult>;

  abstract cancel(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult>;
}
